import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { TESTIMONIALS } from "@/data/content";

export default function Testimonials() {
  const [i, setI] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setI((p) => (p + 1) % TESTIMONIALS.length), 6500);
    return () => clearInterval(t);
  }, []);

  const t = TESTIMONIALS[i];

  return (
    <section className="py-24 md:py-32 px-6 lg:px-10 bg-[#0A2342] text-white relative overflow-hidden" data-testid="testimonials-section">
      {/* Decorative gold ring */}
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full border border-[#C9A227]/20" />
      <div className="absolute -bottom-40 -left-40 w-[28rem] h-[28rem] rounded-full border border-[#C9A227]/10" />

      <div className="max-w-6xl mx-auto relative">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-10 h-px bg-[#C9A227]" />
          <span className="overline text-[#C9A227]">Loved by travellers worldwide</span>
        </div>

        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] max-w-3xl">
          Stories from journeys we've designed.
        </h2>

        <div className="mt-14 grid lg:grid-cols-[1fr_auto] gap-10 items-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl"
            >
              <Quote className="w-10 h-10 text-[#C9A227] mb-6" />
              <p className="text-xl md:text-2xl lg:text-[1.6rem] leading-relaxed font-light text-white/95">
                "{t.quote}"
              </p>

              <div className="mt-8 flex items-center gap-4">
                <img
                  src={t.image}
                  alt={t.name}
                  className="w-14 h-14 rounded-full object-cover border-2 border-[#C9A227]"
                />
                <div>
                  <div className="font-display text-lg">{t.name}</div>
                  <div className="text-white/60 text-sm">{t.trip}</div>
                </div>
                <div className="ml-auto flex gap-0.5">
                  {Array.from({ length: t.rating }).map((_, k) => (
                    <Star key={k} className="w-4 h-4 fill-[#C9A227] text-[#C9A227]" />
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="flex lg:flex-col gap-3">
            <button
              data-testid="testimonial-prev"
              aria-label="Previous"
              onClick={() => setI((p) => (p - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}
              className="w-12 h-12 rounded-full border border-white/20 hover:bg-[#C9A227] hover:border-[#C9A227] transition-all flex items-center justify-center"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              data-testid="testimonial-next"
              aria-label="Next"
              onClick={() => setI((p) => (p + 1) % TESTIMONIALS.length)}
              className="w-12 h-12 rounded-full border border-white/20 hover:bg-[#C9A227] hover:border-[#C9A227] transition-all flex items-center justify-center"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Dots */}
        <div className="mt-10 flex items-center gap-2">
          {TESTIMONIALS.map((_, k) => (
            <button
              key={k}
              onClick={() => setI(k)}
              data-testid={`testimonial-dot-${k}`}
              className={`h-1.5 rounded-full transition-all ${k === i ? "w-8 bg-[#C9A227]" : "w-3 bg-white/25"}`}
              aria-label={`Go to testimonial ${k + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
